/* eslint-disable react/forbid-prop-types */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FiUser } from 'react-icons/fi';
import subYears from 'date-fns/subYears';
import AppTextInput from '../../../../shared/components/app-text-input/AppTextInput';
import AppSelect from '../../../../shared/components/app-select/AppSelect';
import AppDatePicker from '../../../../shared/components/app-date-picker/AppDatePicker';
import { PASSENGER_TITLE_OPTIONS } from '../../constants/esimConstants';
import {
  getNewDate,
  getAPIFormattedDate,
} from '../../../../shared/helper-utils/appUtils';
import {
  calculateAge,
  isValidEmail,
  isValidMobileNumber,
  isValidPassportNumber,
} from '../../utils/esimUtils';
import './EsimTravelerForm.scss';

function FieldWrap({ children }) {
  return <div className="esim-traveler-form__field">{children}</div>;
}

FieldWrap.propTypes = { children: PropTypes.node.isRequired };

function EsimTravelerForm({
  traveler,
  countries,
  onChange,
  showErrors,
  travelStartDate,
}) {
  const setField = (name, value) => onChange({ ...traveler, [name]: value });

  // Min dob: arbitrary cap of 100 yrs old — guard against typos.
  // Max dob: today (we accept infants & children too).
  const dobRange = useMemo(() => {
    const today = new Date();
    return {
      minDate: subYears(today, 100),
      maxDate: today,
    };
  }, []);

  // Passport expiry must be at least the travel start (or today if travel start
  // is missing — we still want the expiry to be in the future).
  const expiryMinRef = travelStartDate
    ? getNewDate(travelStartDate)
    : new Date();

  const setDob = (date) => {
    const iso = getAPIFormattedDate(date);
    const age = calculateAge(date);
    onChange({ ...traveler, dateOfBirth: iso, age });
  };

  const errors = {
    title: showErrors && !traveler.title,
    firstName: showErrors && !traveler.firstName?.trim(),
    lastName: showErrors && !traveler.lastName?.trim(),
    dob: showErrors && !traveler.dateOfBirth,
    nationality: showErrors && !traveler.nationality,
    passportNumber:
      showErrors && !isValidPassportNumber(traveler.passportNumber || ''),
    passportExpiry: showErrors && !traveler.passportExpiry,
    email: showErrors && !isValidEmail(traveler.email),
    mobile:
      showErrors &&
      !isValidMobileNumber(
        traveler.mobile || '',
        traveler.mobileCountryCode || 'IN',
      ),
  };

  const passportErrText = errors.passportNumber
    ? 'Enter a valid passport number (6-12 letters/digits)'
    : '';
  const emailErrText = errors.email ? 'Enter a valid email address' : '';
  const mobileErrText = errors.mobile
    ? 'Enter a valid 10-digit mobile number'
    : '';

  const countryOptions = useMemo(() => {
    if (!Array.isArray(countries)) return [];
    return countries.map((c) => ({
      value: c.value,
      label: c.label,
    }));
  }, [countries]);

  return (
    <section className="esim-traveler-form">
      <header className="esim-traveler-form__head">
        <span className="esim-traveler-form__badge">
          <FiUser />
          Traveller Details
        </span>
        <p className="esim-traveler-form__sub">
          Enter the traveller&apos;s details exactly as they appear on the
          passport. The eSIM will be issued to this person.
        </p>
      </header>

      <div className="esim-traveler-form__grid">
        <FieldWrap>
          <AppSelect
            name="title"
            label="Title"
            required
            isClearable={false}
            placeholder=""
            options={PASSENGER_TITLE_OPTIONS}
            selectedValue={traveler.title || ''}
            onSelect={(opt) => setField('title', opt ? opt.value : '')}
            errorText={errors.title ? 'Title required' : ''}
          />
        </FieldWrap>

        <FieldWrap>
          <AppTextInput
            name="firstName"
            label="First Name"
            required
            value={traveler.firstName || ''}
            onChange={(e) => setField('firstName', e.target.value)}
            errorText={errors.firstName ? 'First name required' : ''}
          />
        </FieldWrap>

        <FieldWrap>
          <AppTextInput
            name="lastName"
            label="Last Name"
            required
            value={traveler.lastName || ''}
            onChange={(e) => setField('lastName', e.target.value)}
            errorText={errors.lastName ? 'Last name required' : ''}
          />
        </FieldWrap>

        <FieldWrap>
          <AppDatePicker
            name="dateOfBirth"
            label="Date of Birth"
            required
            customClassName={`position-relative flight-booking-picker ${
              errors.dob ? 'app-error-text-border' : ''
            }`}
            className="app-date-picker"
            labelClassName="booking-date-label"
            placeholder=""
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            minDate={dobRange.minDate}
            maxDate={dobRange.maxDate}
            selectedDate={
              traveler.dateOfBirth ? getNewDate(traveler.dateOfBirth) : ''
            }
            onChange={setDob}
          />
          {errors.dob && (
            <small className="esim-traveler-form__err">DOB required</small>
          )}
        </FieldWrap>

        <FieldWrap>
          <AppTextInput
            name="age"
            label="Age"
            disabled
            value={
              traveler.age === '' || traveler.age == null
                ? ''
                : String(traveler.age)
            }
            onChange={() => {}}
          />
        </FieldWrap>

        <FieldWrap>
          <AppSelect
            name="nationality"
            label="Nationality"
            required
            isClearable={false}
            placeholder="Select nationality"
            options={countryOptions}
            selectedValue={traveler.nationality || ''}
            onSelect={(opt) => setField('nationality', opt ? opt.value : '')}
            maxheight="240px"
            errorText={errors.nationality ? 'Nationality required' : ''}
          />
        </FieldWrap>

        <FieldWrap>
          <AppTextInput
            name="passportNumber"
            label="Passport Number"
            required
            value={traveler.passportNumber || ''}
            onChange={(e) =>
              setField(
                'passportNumber',
                String(e.target.value || '')
                  .replace(/[^A-Za-z0-9]/g, '')
                  .toUpperCase(),
              )
            }
            errorText={passportErrText}
          />
          {errors.passportNumber && (traveler.passportNumber || '').trim() && (
            <small className="esim-traveler-form__err">{passportErrText}</small>
          )}
        </FieldWrap>

        <FieldWrap>
          <AppDatePicker
            name="passportExpiry"
            label="Passport Expiry"
            required
            customClassName={`position-relative flight-booking-picker ${
              errors.passportExpiry ? 'app-error-text-border' : ''
            }`}
            className="app-date-picker"
            labelClassName="booking-date-label"
            placeholder="DD/MM/YYYY"
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            minDate={expiryMinRef}
            selectedDate={
              traveler.passportExpiry ? getNewDate(traveler.passportExpiry) : ''
            }
            onChange={(date) =>
              setField('passportExpiry', getAPIFormattedDate(date))
            }
          />
          {errors.passportExpiry && (
            <small className="esim-traveler-form__err">Expiry required</small>
          )}
        </FieldWrap>

        <FieldWrap>
          <AppTextInput
            name="email"
            label="Email"
            required
            type="email"
            value={traveler.email || ''}
            onChange={(e) => setField('email', e.target.value)}
            errorText={emailErrText}
          />
          {errors.email && (traveler.email || '').trim() && (
            <small className="esim-traveler-form__err">{emailErrText}</small>
          )}
        </FieldWrap>

        <FieldWrap>
          <AppTextInput
            name="mobile"
            label="Mobile Number"
            required
            inputmode="numeric"
            maxLength={10}
            value={traveler.mobile || ''}
            onChange={(e) =>
              setField(
                'mobile',
                String(e.target.value || '')
                  .replace(/\D/g, '')
                  .slice(0, 10),
              )
            }
            errorText={mobileErrText}
          />
          {errors.mobile && (traveler.mobile || '').trim() && (
            <small className="esim-traveler-form__err">{mobileErrText}</small>
          )}
        </FieldWrap>
      </div>
    </section>
  );
}

EsimTravelerForm.propTypes = {
  traveler: PropTypes.object.isRequired,
  countries: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  showErrors: PropTypes.bool,
  travelStartDate: PropTypes.string,
};

EsimTravelerForm.defaultProps = {
  countries: [],
  showErrors: false,
  travelStartDate: '',
};

export default EsimTravelerForm;
